import { trpc } from '@/lib/api/trpc';
import { useSessionClaims } from './use-session-claims';

/**
 * Hook for partner-scoped admin views (Revenue / Processing / Crypto
 * under /org/<slug>/admin/partner/<partner>/*).
 *
 * Access rules:
 * - Superadmin: can view any partner's dashboard (passes `requestedPartner`
 *   through unchanged).
 * - partneradmin: can view only the partner matching their
 *   `whitelabelScope` (stored as `whitelabelOwnership` in Clerk metadata).
 * - Anyone else: denied.
 *
 * `canViewRx` is the TagadaRx partner desk gate (`/rx/<slug>`):
 * superadmin, unscoped Rx staff (any partner), or Rx staff scoped to
 * that partner. A partneradmin does NOT get it for free — the Rx option
 * is the `rx_admin` grant (see `canAssignTgdRole`).
 *
 * Primary path: reads from session-token claims — no network hop.
 *
 * Transition fallback (Phase 1 of tgdRoles → publicMetadata migration):
 * if the session token was issued before the Dashboard template flip,
 * `tgdRoles`/`whitelabelScope` are absent from the claim. We lazily hit
 * `trpc.users.getCurrentUserInfo` when claims are loaded but empty so a
 * legitimate partneradmin isn't locked out of their own scope during the
 * rollout. Safe to remove in Phase 3 once all sessions have rolled over.
 */
export function usePartnerAdmin(requestedPartner?: string) {
    const {
        isLoaded: claimsLoaded,
        isSuperAdmin: claimIsSuperAdmin,
        isPartnerAdmin: claimIsPartnerAdmin,
        whitelabelScope: claimWhitelabelScope,
        isRxStaff,
        tgdRoles,
    } = useSessionClaims();

    const needsFallback = claimsLoaded && tgdRoles.length === 0;

    const { data: userInfo, isLoading: trpcLoading } = trpc.users.getCurrentUserInfo.useQuery(
        {},
        {
            enabled: needsFallback,
            staleTime: 5 * 60 * 1000,
            gcTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: 1,
        },
    );

    const isSuperAdmin = claimIsSuperAdmin || !!userInfo?.isSuperAdmin;
    const isPartnerAdmin = claimIsPartnerAdmin || !!userInfo?.isPartnerAdmin;
    const whitelabelScope = claimWhitelabelScope ?? userInfo?.whitelabelOwnership ?? null;

    // Effective partner for this view:
    // - Superadmin: whatever was requested (via URL param)
    // - partneradmin: always their own scope, regardless of URL
    const isPlatformRxStaff = isRxStaff && !whitelabelScope;
    const effectivePartner = isSuperAdmin || isPlatformRxStaff
        ? (requestedPartner ?? whitelabelScope)
        : whitelabelScope;

    const canView = isSuperAdmin
        ? !!effectivePartner
        : (isPartnerAdmin && !!whitelabelScope && whitelabelScope === requestedPartner);

    const canViewRx = isSuperAdmin || isPlatformRxStaff
        ? !!effectivePartner
        : (isRxStaff && !!whitelabelScope && whitelabelScope === requestedPartner);

    return {
        canView,
        canViewRx,
        partner: effectivePartner,
        isSuperAdmin,
        isPartnerAdmin,
        whitelabelOwnership: whitelabelScope,
        isLoading: !claimsLoaded || (needsFallback && trpcLoading),
    };
}
